import PropTypes from 'prop-types';
import React from 'react';
import styled from '@emotion/styled';
import { t } from 'services/translation';
import pluralize from 'pluralize';
import Dropdown from 'views/ui/form_elements/dropdown';
import moment from 'moment-timezone';

import { Theme } from 'ui_system/theme';
import { timezoneList } from 'ui_system/utilities/timezoneUtils';
import Hint from './hint';

import './index.scss';

const TimezoneLabel = styled.div`
  color: ${Theme.colors.black};
  margin-bottom: 25px;

  span {
    color: ${Theme.colors.green};
    cursor: pointer;
  }
`;

const DropdownWrapper = styled.div`
  width: 185px;
`;

const PLACES_LEFT_THRESHOLD = 8;

const sortTimes = (a, b) => {
  const offset = (time) => {
    if (time.match(/.*pm/i)) {
      if (!time.trim().match(/^12/)) {
        return 12;
      }
    } else if (time.match(/.*am/i)) {
      if (time.trim().match(/^12/)) {
        return -12;
      }
    }
    return 0;
  };
  const toFloat = (time) =>
    parseFloat(time.replace(':', '.').replace(/[^\d.-]/g, '')) + offset(time);
  return toFloat(a) - toFloat(b);
};

// Takes a time string and returns the ISO/24-hour time
export const toISOTime = (time) => {
  return moment(time, 'h:mma').format('HH:mm');
};

class Times extends React.PureComponent {
  static displayName = 'BookingRequest-Times';

  static propTypes = {
    times: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    guests: PropTypes.number.isRequired,
    showPlacesLeft: PropTypes.bool,
    displaySingleTime: PropTypes.bool,
    value: PropTypes.string,
    errors: PropTypes.array,
    forceDropdown: PropTypes.bool,
    hideLabel: PropTypes.bool,
    isoTime: PropTypes.bool,
    timezonesEnabled: PropTypes.bool,
    timezone: PropTypes.string,
    openTimezoneModal: PropTypes.func,
    hasTicketCapacityEnabled: PropTypes.bool,
  };

  static defaultProps = {
    time: null,
    errors: [],
    displaySingleTime: true,
    forceDropdown: false,
    hideLabel: false,
    isoTime: false,
    timezonesEnabled: false,
    timezone: '',
    openTimezoneModal: () => {},
    hasTicketCapacityEnabled: false,
  };

  dropdownProps = () => {
    const { value, name, onChange, hasTicketCapacityEnabled } = this.props;

    return {
      options: this.optionsForDropdown(),
      disabledOptionKeys: this.disabledOptionKeysForDropdown(),
      subLabels: this.subLabelsForDropdown(),
      hasTicketCapacityEnabled: hasTicketCapacityEnabled,
      value: value,
      name: name,
      onChange: onChange,
      placeholder: t('SELECT'),
      icon: <i className="af-angle-down-ar icon af-triangle-down-2" />,
      displayOptions: {
        compact: false,
      },
      'data-testid': 'time-dropdown',
    };
  };

  optionsForDropdown = () => {
    const options = {};

    this._timeKeys()
      .sort(sortTimes)
      .forEach((time) => {
        if (this.props.isoTime) {
          // Iso/24-hour time display
          options[time] = toISOTime(time);
        } else {
          // AM-PM display
          let label = time.toUpperCase();
          label = label.replace('AM', ` ${t('AM')}`);
          label = label.replace('PM', ` ${t('PM')}`);
          options[time] = label;
        }
      });

    return options;
  };

  disabledOptionKeysForDropdown = () => {
    const disabledOptionKeys = {};
    let spotsLeft = null;
    const _this = this;

    this._timeKeys().forEach((time) => {
      spotsLeft = _this.props.times[time];

      if (_this._isSoldOut(spotsLeft)) {
        disabledOptionKeys[time] = true;
      }
    });

    return disabledOptionKeys;
  };

  subLabelsForDropdown = () => {
    const subLabels = {};
    let spotsLeft = null;
    const _this = this;

    this._timeKeys().forEach((time) => {
      spotsLeft = _this.props.times[time];

      if (spotsLeft <= 0) {
        subLabels[time] = `${t('SOLD_OUT')}`;
      } else if (spotsLeft < PLACES_LEFT_THRESHOLD) {
        subLabels[time] = `${spotsLeft} ${t(pluralize('spot', spotsLeft))}
            ${t('left')}`;
      }
    });

    return subLabels;
  };

  componentDidMount() {
    this._selectSingleTime();
  }

  componentDidUpdate(prevProps) {
    const { date, times } = this.props;
    const { date: prevDate } = prevProps;

    if (date && prevDate && times) {
      if (date !== prevDate) {
        if (Object.keys(times).length <= 1) {
          this._selectSingleTime();
        } else {
          this.props.onChange(this.props.name, null);
        }
      }
    } else {
      this._selectSingleTime();
    }

    if (this.props.errors.length > 0) {
      $(this.refs.label).transition({
        animation: 'shake',
        duration: '600ms',
      });
    }
  }

  onTimeClick = (e) => {
    let { value } = e.currentTarget.attributes['data-value'];
    if (value === this.props.value) {
      value = null;
    }
    this.props.onChange(this.props.name, value);
  };

  _selectSingleTime = () => {
    const allTimes = this._timeKeys();
    if (allTimes.length === 1) {
      this.props.onChange(this.props.name, allTimes[0]);
    }
  };

  _isSoldOut = (placesLeft) => {
    return placesLeft < this.props.guests;
  };

  _timeKeys = () => {
    return Object.keys(this.props.times);
  };

  _hasMultipleTimes = () => {
    return this.props.times && this._timeKeys().length > 1;
  };

  _renderTranslatedTime = (time) => {
    time = time.trim();
    const onlyTime = time.slice(0, -2); // 9:00
    const am_pm = time.slice(-2); // am or pm
    return onlyTime + t(am_pm);
  };

  _renderTime = (time, index) => {
    const placesLeft = this.props.times[time];
    let columnClass;
    let iconClass;
    let clickHandler;

    if (placesLeft === null || placesLeft === undefined) {
      // This shouldn't be possible since the BookingDetails component makes
      // sure we have times with available slots.
      return null;
    }

    columnClass = `${
      window.mobileDetect.phone() ? 'six' : 'four'
    } wide column time-wrapper`;

    if (this.props.value === time) {
      iconClass = 'af-checkmark brand-bg';
    }

    if (placesLeft <= 8) {
      columnClass += ' with-hint';
    }
    if (this._isSoldOut(placesLeft)) {
      columnClass += ' sold-out';
      iconClass = '';
      if (this.props.value === time) {
        this.props.onChange(this.props.name, null);
      }
    } else if (this.props.value !== time) {
      clickHandler = this.onTimeClick;
    }

    let displayTime = this._renderTranslatedTime(time);
    if (this.props.isoTime) {
      displayTime = toISOTime(time);
    }

    return (
      <button
        type="button"
        data-value={time}
        key={index}
        className={columnClass}
        onClick={clickHandler}
        data-testid="time-wrapper"
      >
        <div className="time" data-testid="start-time-control">
          <div className="text">
            <i className={`icon ${iconClass}`} data-testid="time-slot" />
            <span className="actualTimeText">{displayTime}</span>
            <br />
            <Hint
              spots={placesLeft >= 0 ? placesLeft : 0}
              isSoldOut={this._isSoldOut(placesLeft)}
              showPlacesLeft={this.props.showPlacesLeft}
            />
          </div>
        </div>
      </button>
    );
  };

  _renderSingleTime = (time) => {
    const placesLeft = this.props.times[time];

    let displayTime = this._renderTranslatedTime(time);
    if (this.props.isoTime) {
      displayTime = toISOTime(time);
    }

    return (
      <div className="column BookingRequest-Times-time-single">
        {displayTime}
        <br />
        <Hint
          spots={placesLeft >= 0 ? placesLeft : 0}
          isSoldOut={this._isSoldOut(placesLeft)}
          showPlacesLeft={this.props.showPlacesLeft}
        />
      </div>
    );
  };

  _renderTimes = () => {
    if (this.props.times) {
      return this._timeKeys().sort(sortTimes).map(this._renderTime);
    }
    return (
      <p className="twelve wide column">No times available for selected date</p>
    );
  };

  render() {
    const { timezone, openTimezoneModal, timezonesEnabled, date } = this.props;

    let text;
    let view;
    let displayTimezone = false;

    const timesCount = this._timeKeys().length;
    if (
      ((!this.props.dateSelected || timesCount === 0) &&
        this.props.datesAvailable) ||
      this.props.loadingDates
    ) {
      // Default view: before selecting a date
      // ===========
      text = t('START_TIME');
      view = (
        <div className="ui grid BookingRequest-times">
          <p className="twelve wide column">
            {t('SELECT_A_DATE_ABOVE_TO_SEE_AVAILABLE_TIMES')}
          </p>
        </div>
      );
    } else if (!this.props.forceDropdown && timesCount === 1) {
      // Scenario 1: Single time rendered as text (1 time option)
      // ===========
      if (this.props.displaySingleTime) {
        text = t('START_TIME');
        displayTimezone = true;
        view = (
          <div className="ui grid BookingRequest-times">
            {this._renderSingleTime(this._timeKeys()[0])}
          </div>
        );
      } else {
        return null;
      }
    } else if (!this.props.forceDropdown && timesCount > 1 && timesCount <= 6) {
      // Scenario 2: Grid view of timeslots (2 - 6 time options)
      // ===========
      text = t('SELECT_THE_DESIRED_START_TIME');
      displayTimezone = true;
      view = (
        <div
          className="ui grid BookingRequest-times"
          role="group"
          aria-labelledby="time-label"
        >
          {this._renderTimes()}
        </div>
      );
    } else if (this.props.forceDropdown || timesCount > 6) {
      // Scenario 3: Dropdown select for timeslots (7+ time options)
      // ===========
      text = t('SELECT_THE_DESIRED_START_TIME');
      displayTimezone = true;
      view = (
        <DropdownWrapper>
          <Dropdown key={date} {...this.dropdownProps()} />
        </DropdownWrapper>
      );
    } else {
      return null;
    }

    displayTimezone = displayTimezone && timezonesEnabled;
    const selectedTimezone = timezoneList[timezone];

    return (
      <div data-testid="time-field" className="field">
        {!this.props.hideLabel && (
          <label ref="label" id="time-label">
            {text}
          </label>
        )}
        {displayTimezone && (
          <TimezoneLabel>
            {t('TIMES_SHOWN_IN')}{' '}
            <span className="brand-color" onClick={openTimezoneModal}>
              {selectedTimezone.name}
            </span>
          </TimezoneLabel>
        )}
        {view}
      </div>
    );
  }
}

export default Times;
